/**
 * Capture policy for session replay.
 *
 * Isolated in its own file on purpose: this is the file a reviewer (or an
 * auditor) should be able to read on its own and decide whether recording this
 * product is safe. Nothing else in the replay stack decides what gets captured.
 *
 * V1 records readable text, attributes and ordinary input values. Replay access
 * is dev-only and MFA-gated; password values remain masked by rrweb. The helper
 * functions below are retained for a future masked mode, but the live recorder
 * does not run the attribute scrubber or blanket text masking.
 */

/** Add to an element to record its text verbatim. Use for static chrome only. */
export const UNMASK_CLASS = 'cpt-replay-unmask';

/** Add to an element to leave it out of the recording entirely (renders as a placeholder box). */
export const BLOCK_CLASS = 'cpt-replay-block';

/** Add to an input to ignore its value changes completely. */
export const IGNORE_CLASS = 'cpt-replay-ignore';

/**
 * Replace every character of every text node with a fixed glyph, preserving
 * length so the layout still reads.
 *
 * Length is itself a (very weak) signal, but preserving it is what keeps a
 * replay legible — a note editor collapsed to a single dot tells you nothing
 * about whether the user actually typed anything. Cap it so a 40-page note
 * doesn't turn into 40 pages of dots.
 */
const MAX_MASK_LENGTH = 200;

export function maskText(text, element) {
  if (!text) return text;
  if (element && element.classList && element.classList.contains(UNMASK_CLASS)) {
    return text;
  }
  const length = Math.min(text.length, MAX_MASK_LENGTH);
  return '•'.repeat(length);
}

/**
 * Attribute values rrweb does NOT mask.
 *
 * This is the gap that makes the scrubber below necessary. rrweb 2.1.1's record
 * options (checked against node_modules/rrweb/dist/rrweb.d.ts) expose
 * maskTextFn and maskInputFn — and nothing for attributes. There is no
 * maskAttributeFn in this version. So `<span title="Jane Doe, DOB 1984-02-11">`
 * has its TEXT masked and its TITLE recorded verbatim, in both the initial full
 * snapshot and every later mutation.
 *
 * Attributes are therefore scrubbed by us, after rrweb emits and before the
 * event is buffered, in scrubEventAttributes(). Same allowlist reasoning as the
 * text masking: strip anything that carries human-readable content, keep only
 * what the replayer needs to lay the page out.
 */
const SCRUBBED_ATTRIBUTES = new Set([
  'title',
  'alt',
  'placeholder',
  'value',
  'label',
  'aria-label',
  'aria-labelledby',
  'aria-description',
  'aria-describedby',
  'aria-placeholder',
  'aria-valuetext',
  'content',
  'download',
  'name',
]);

/** Attributes whose value is a URL — keep the shape, drop the query string. */
const URL_ATTRIBUTES = new Set(['href', 'src', 'srcset', 'action', 'poster']);

const SCRUB_PLACEHOLDER = '••';

function scrubAttributeValue(key, value) {
  if (typeof value !== 'string' || value === '') return value;
  // data-* is where apps stash record ids, patient names and serialized state.
  if (SCRUBBED_ATTRIBUTES.has(key) || key.startsWith('data-')) {
    return SCRUB_PLACEHOLDER;
  }
  if (URL_ATTRIBUTES.has(key)) {
    const queryStart = value.indexOf('?');
    return queryStart === -1 ? value : value.slice(0, queryStart);
  }
  return value;
}

function scrubAttributeMap(attributes) {
  if (!attributes || typeof attributes !== 'object') return;
  for (const key of Object.keys(attributes)) {
    attributes[key] = scrubAttributeValue(key, attributes[key]);
  }
}

function scrubNodeTree(node) {
  if (!node || typeof node !== 'object') return;
  scrubAttributeMap(node.attributes);
  if (Array.isArray(node.childNodes)) node.childNodes.forEach(scrubNodeTree);
}

/**
 * Strip attribute values from one rrweb event, in place.
 *
 * Handles the two event shapes that carry attributes:
 *   type 2 (FullSnapshot)  → data.node, a nested tree
 *   type 3 (IncrementalSnapshot) with data.source 0 (Mutation)
 *                          → data.attributes, a flat list of {id, attributes}
 *                            plus data.adds, whose nodes are full subtrees
 *
 * Mutates rather than clones: these events are large and this runs on every one
 * of them. The event has not been handed to anything else at this point.
 *
 * Deliberately fails CLOSED — if anything in here throws, the caller drops the
 * event rather than buffering one that may not have been scrubbed.
 */
export function scrubEventAttributes(event) {
  if (!event || typeof event !== 'object' || !event.data) return event;

  if (event.type === 2) {
    scrubNodeTree(event.data.node);
    return event;
  }

  if (event.type === 3 && event.data.source === 0) {
    if (Array.isArray(event.data.attributes)) {
      event.data.attributes.forEach((mutation) =>
        scrubAttributeMap(mutation && mutation.attributes),
      );
    }
    if (Array.isArray(event.data.adds)) {
      event.data.adds.forEach((add) => scrubNodeTree(add && add.node));
    }
  }

  return event;
}

/**
 * rrweb record options. Passed straight through — keep this the single source
 * of truth rather than spreading options at the call site.
 */
export function buildRecordOptions() {
  return {
    // Record ordinary values, but never record password fields.
    maskAllInputs: false,
    maskInputOptions: { password: true },
    blockClass: BLOCK_CLASS,
    ignoreClass: IGNORE_CLASS,
    maskTextClass: 'cpt-replay-mask',

    // No checkoutEveryNms here, deliberately. Measured against a real 14-minute
    // capture, periodic snapshots made the viewer WORSE (every seek across one
    // rebuilds the whole DOM and every iframe in it: 4 rebuilds became 56) and
    // cost snapshot bytes at capture. The EMR recorder re-snapshots only because
    // its drained batches can be lost — see EmrReplayRecorder.ts.

    // Canvas replay is expensive and is not needed for the first useful pass.
    recordCanvas: false,
    // Cross-origin iframes cannot be recorded anyway; saying so explicitly
    // documents that the EMR page around us is NOT in the recording.
    recordCrossOriginIframes: false,
    collectFonts: false,

    // Inline our own stylesheets so the replay renders without reaching back to
    // app.comprehendpt.com for CSS that may have shipped three versions ago.
    inlineStylesheet: true,
    // Images are the single biggest contributor to event size.
    inlineImages: false,

    slimDOMOptions: {
      script: true,
      comment: true,
      headFavicon: true,
      headWhitespace: true,
      headMetaSocial: true,
      headMetaRobots: true,
      headMetaHttpEquiv: true,
      headMetaVerification: true,
    },
  };
}
